import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useToastOptional } from '@/components/ui/Toast'
import { describeError } from '@/lib/queryError'

/**
 * WP-05: a background refetch that fails (the data is still on screen) says so in a toast
 * instead of silently showing stale numbers. A first load that fails is the page's ErrorState,
 * not a toast. Queries with `meta: { silent: true }` (the rail's badge counts) never toast.
 * Mounted once in the shell; it listens to whichever QueryClient is above it (the
 * app's or a story's), so no global wiring is needed. One toast per query key and minute.
 */
export function QueryErrorToasts() {
  const client = useQueryClient()
  const toast = useToastOptional()
  const shown = useRef(new Map<string, number>())

  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        if (toast === null) return
        if (event.type !== 'updated' || event.action.type !== 'error') return
        const q = event.query
        if (q.state.data === undefined) return // first load: the page shows ErrorState
        if ((q.meta as { silent?: boolean } | undefined)?.silent) return // a rail badge: no badge is enough
        const now = Date.now()
        const last = shown.current.get(q.queryHash) ?? 0
        if (now - last < 60_000) return
        shown.current.set(q.queryHash, now)
        toast.error(`Данные не обновились (код ${describeError(event.action.error).code}). На экране — последние полученные.`)
      }),
    [client, toast],
  )
  return null
}
